/**
 * `.zwdoc` → readable Markdown for the Markdown export target (#258).
 *
 * CommonMark + GFM where it has syntax (emphasis, strike, links, images,
 * lists, quotes, code, rules), the conventions Zephyrwrite used before
 * `.zwdoc` elsewhere (`==highlight==`, `<u>underline</u>`), raw blocks
 * verbatim. Best-effort only: alignment and indent are dropped, a spacer
 * becomes an `&nbsp;` paragraph (a visible gap), comments are omitted.
 */

import type { Mark, Node as PMNode } from "prosemirror-model";
import { isJust, just, nothing, type Maybe } from "../lib/maybe";
import { withoutAllComments } from "../lib/zwdoc/comments";

/** Opening order; closing is the reverse. Code isn't here: it's atomic. */
const MARK_ORDER = [
  "link",
  "bold",
  "italic",
  "strike",
  "underline",
  "highlight",
] as const;

/** A code span with a fence longer than any backtick run inside it. */
function codeSpan(text: string): string {
  const longest = Math.max(
    0,
    ...[...text.matchAll(/`+/g)].map((m) => m[0].length),
  );
  const fence = "`".repeat(longest + 1);
  const pad = text.startsWith("`") || text.endsWith("`") ? " " : "";
  return `${fence}${pad}${text}${pad}${fence}`;
}

function markRank(mark: Mark): number {
  const rank = MARK_ORDER.indexOf(
    mark.type.name as (typeof MARK_ORDER)[number],
  );
  return rank === -1 ? MARK_ORDER.length : rank;
}

function openMark(mark: Mark): string {
  switch (mark.type.name) {
    case "link":
      return "[";
    case "bold":
      return "**";
    case "italic":
      return "*";
    case "strike":
      return "~~";
    case "underline":
      return "<u>";
    case "highlight":
      return "==";
    default:
      return "";
  }
}

function linkTarget(mark: Mark): string {
  const href = String(mark.attrs.href).replace(/[()\s]/g, encodeURIComponent);
  const title = String(mark.attrs.title);
  return title === "" ? href : `${href} "${title.replace(/"/g, '\\"')}"`;
}

function closeMark(mark: Mark): string {
  switch (mark.type.name) {
    case "link":
      return `](${linkTarget(mark)})`;
    case "underline":
      return "</u>";
    default:
      return openMark(mark);
  }
}

/** Characters that would otherwise read as inline Markdown. */
function escapeInline(text: string): string {
  return text.replace(/[\\`*_[\]<>~=|]/g, "\\$&");
}

/** Escape what would start a block construct at the beginning of a line. */
function escapeLineStart(line: string): string {
  return line
    .replace(/^(\s*)([#>+-])(?=\s|$)/, "$1\\$2")
    .replace(/^(\s*)(\d+)([.)])(?=\s|$)/, "$1$2\\$3");
}

type InlineItem =
  | { kind: "text"; text: string; marks: readonly Mark[]; code: boolean }
  | { kind: "break" }
  | { kind: "image"; alt: string; src: string; title: string };

function inlineItems(block: PMNode): InlineItem[] {
  const items: InlineItem[] = [];
  block.forEach((child) => {
    if (child.isText) {
      items.push({
        kind: "text",
        text: child.text ?? "",
        marks: child.marks
          .filter((mark) => mark.type.name !== "code")
          .sort((a, b) => markRank(a) - markRank(b)),
        code: child.marks.some((mark) => mark.type.name === "code"),
      });
    } else if (child.type.name === "hard_break") {
      items.push({ kind: "break" });
    } else if (child.type.name === "image") {
      items.push({
        kind: "image",
        alt: String(child.attrs.alt),
        src: String(child.attrs.src),
        title: String(child.attrs.title),
      });
    }
  });
  return items;
}

/**
 * Inline content with marks opened / closed as a stack, so adjacent runs
 * sharing a mark stay inside one delimiter pair. Whitespace is kept outside
 * delimiters (`**word** ` not `**word **`), which CommonMark requires.
 */
function serializeInline(block: PMNode): string {
  let out = "";
  const active: Mark[] = [];

  const closeTo = (keep: number): void => {
    const trailing = /\s*$/.exec(out)?.[0] ?? "";
    out = out.slice(0, out.length - trailing.length);
    while (active.length > keep) {
      const mark = active.pop();
      if (typeof mark !== "undefined") out += closeMark(mark);
    }
    out += trailing;
  };

  for (const item of inlineItems(block)) {
    if (item.kind === "break") {
      closeTo(0);
      out += "\\\n";
      continue;
    }
    if (item.kind === "image") {
      closeTo(0);
      const title = item.title === "" ? "" : ` "${item.title}"`;
      out += `![${escapeInline(item.alt)}](${item.src.replace(/[()\s]/g, encodeURIComponent)}${title})`;
      continue;
    }
    let keep = 0;
    while (
      keep < active.length &&
      keep < item.marks.length &&
      active[keep]?.eq(item.marks[keep] as Mark) === true
    ) {
      keep += 1;
    }
    closeTo(keep);
    const body = item.code ? codeSpan(item.text) : escapeInline(item.text);
    const leading = /^\s*/.exec(body)?.[0] ?? "";
    out += leading;
    for (const mark of item.marks.slice(keep)) {
      out += openMark(mark);
      active.push(mark);
    }
    out += body.slice(leading.length);
  }
  closeTo(0);
  // Leading tabs / 4+ spaces would make an indented code block; a writer's
  // first-line indent has no Markdown equivalent, so drop it.
  return out
    .split("\n")
    .map((line) => escapeLineStart(line.replace(/^[ \t]+/, "")))
    .join("\n");
}

function prefixLines(text: string, first: string, rest: string): string {
  return text
    .split("\n")
    .map((line, i) => (line === "" ? line : `${i === 0 ? first : rest}${line}`))
    .join("\n");
}

function serializeBlocks(parent: PMNode): string[] {
  const out: string[] = [];
  parent.forEach((block) => {
    const text = serializeBlock(block);
    if (isJust(text)) out.push(text.value);
  });
  return out;
}

function serializeBlock(block: PMNode): Maybe<string> {
  switch (block.type.name) {
    case "paragraph": {
      const text = serializeInline(block);
      return text.trim() === "" ? nothing : just(text);
    }
    case "heading": {
      const text = serializeInline(block).replace(/\\\n/g, " ");
      return just(`${"#".repeat(Number(block.attrs.level))} ${text}`);
    }
    case "blockquote":
      return just(
        serializeBlocks(block)
          .join("\n\n")
          .split("\n")
          .map((line) => (line === "" ? ">" : `> ${line}`))
          .join("\n"),
      );
    case "bullet_list":
    case "ordered_list": {
      const ordered = block.type.name === "ordered_list";
      let n = Number(block.attrs.start ?? 1);
      const items: string[] = [];
      block.forEach((item) => {
        const marker = ordered ? `${n}. ` : "- ";
        n += 1;
        // A nested list sits directly under its item's text (tight list).
        let body = "";
        item.forEach((child, _offset, index) => {
          const text = serializeBlock(child);
          if (isJust(text) === false) return;
          const nestedList = child.type.name.endsWith("_list");
          body += index === 0 ? "" : nestedList ? "\n" : "\n\n";
          body += text.value;
        });
        items.push(prefixLines(body, marker, " ".repeat(marker.length)));
      });
      return just(items.join("\n"));
    }
    case "code_block": {
      const text = block.textContent;
      const fence = "`".repeat(
        Math.max(3, ...[...text.matchAll(/`+/g)].map((m) => m[0].length + 1)),
      );
      return just(`${fence}${String(block.attrs.language)}\n${text}\n${fence}`);
    }
    case "horizontal_rule":
      return just("---");
    case "raw":
      return just(String(block.attrs.markdown));
    case "spacer":
      return just("&nbsp;");
    default:
      return nothing;
  }
}

/** The document body as Markdown (comments omitted). */
export function zwdocToMarkdown(doc: PMNode): string {
  return serializeBlocks(withoutAllComments(doc)).join("\n\n");
}
